import React, { useEffect, useState } from 'react';
import EarthHomeWorldIcon from '@/assets/Icons/Earth-Home-World-2.svg?react';
import classNames from 'classnames';
import styles from './LanguageMenu.module.scss';
import { map, pick } from 'lodash';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import languages from '@/constants/languages.ts';
import { updateUser } from '@/api/user.ts';
import usePersistentPreferencesStore from '@/store/usePersistentPreferencesStore.ts';
import { toast } from 'react-toastify';
import { useFloating, offset, shift, FloatingPortal, autoUpdate } from '@floating-ui/react';
import OutsideClickHandler from 'react-outside-click-handler';

interface LanguageMenuProps {
	showDropdown?: boolean;
}

const LanguageMenu: React.FC<LanguageMenuProps> = ({ showDropdown }) => {
	const {
		setWeGlotLanguageCode,
		weGlotLanguageCode: selectedLanguageCode,
		loggedIn,
	} = useWhoamiStore(state => state);
	const [isPopoverShown, setIsPopoverShown] = useState<boolean>(false);
	const [prevShowDropdown, setPrevShowDropdown] = useState(false);
	const { isLanguageNotificationSeen } = usePersistentPreferencesStore();

	// Accessing the `Weglot` object from the global window, loaded by WeglotProvider.tsx
	const Weglot = window?.Weglot;

	// State to store the available languages (for rendering dropdown)
	const [availableLanguages, setAvailableLanguages] = useState({});

	const { refs, floatingStyles } = useFloating({
		placement: 'bottom-end',
		middleware: [offset({ mainAxis: 20, crossAxis: 0 }), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	// Show language menu if change language info is seen and closed
	useEffect(() => {
		if (!prevShowDropdown && showDropdown) {
			if (!isPopoverShown && isLanguageNotificationSeen && !loggedIn) {
				setIsPopoverShown(true);
			}
		}
		setPrevShowDropdown(showDropdown || false);
	}, [showDropdown, prevShowDropdown, isPopoverShown, loggedIn, isLanguageNotificationSeen]);

	useEffect(() => {
		// If Weglot is not yet loaded, skip
		if (!Weglot?.options?.languages) {
			return;
		}

		// Get all supported Weglot language codes
		const supportedWeGlotCodes = Weglot?.options?.languages
			.filter(({ enabled = false }) => enabled)
			.map(({ language_to }) => language_to);

		// Get all language key-values pairs (including English)
		// Format: [{languageCode: languageName}]
		setAvailableLanguages(pick(languages, ['en', ...supportedWeGlotCodes]));
	}, [Weglot?.options?.languages]);

	const handleLanguageChange = async (weGlotLanguageCode: string) => {
		// Hide the dropdown
		setIsPopoverShown(false);

		// Set the selected language code in the store
		setWeGlotLanguageCode(weGlotLanguageCode);

		if (loggedIn) {
			// If the user is logged-in, send a request to update his language code
			try {
				await updateUser({
					weGlotLanguageCode,
				});
			} catch {
				toast.error('Something went wrong. Please try again later.');
			}
		}
	};

	return (
		<>
			<div
				className={styles.iconWrapper}
				ref={refs.setReference}
				onClick={() => setIsPopoverShown(!isPopoverShown)}
				key={selectedLanguageCode}
			>
				<EarthHomeWorldIcon />
				<span className={styles.selectedLang}>{selectedLanguageCode}</span>
			</div>
			{isPopoverShown && (
				<FloatingPortal>
					<OutsideClickHandler onOutsideClick={() => setIsPopoverShown(false)}>
						<div
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								transition: 'opacity 0.2s ease-in-out',
							}}
							className={styles.popover}
						>
							<p className={styles.langTitle}>Language</p>
							{map(availableLanguages, (langName, langCode) => (
								<div
									className={classNames(
										styles.popupItem,
										selectedLanguageCode === langCode && styles.active,
									)}
									key={langCode}
									onClick={() => handleLanguageChange(langCode)}
								>
									{langName}
								</div>
							))}
							<div className={styles.additionalInfo}>
								*Translations have been generated using AI, and may show occasional discrepancies with
								original content
							</div>
						</div>
					</OutsideClickHandler>
				</FloatingPortal>
			)}
		</>
	);
};

export default LanguageMenu;
